import {
  AccountType,
  AddCashbackToBalanceParams,
  CashbackFormValues,
  CashbackSectionVisibilityParams,
  CashbackMode,
  CashbackSettings,
  CashbackSourceAccount,
  CashbackSourceOption,
  CashbackSourceOptionsParams,
  CashbackInUseParams,
} from "./types";
import { isBlank } from "./taxWithheld";

export const CASHBACK_CATEGORY_LABEL = "Cashback";
export const cashbackCategoryOf = <C extends { label: string }>(
  categories: C[] | undefined,
): C | undefined =>
  categories?.find(({ label }) => label === CASHBACK_CATEGORY_LABEL);
export const cashbackAccountTypes = [
  AccountType.ACCOUNT,
  AccountType.CHECKING,
  AccountType.SAVINGS,
];
// Earns cashback at all (source pickers, buy funding); only CASH takes a rate and cap.
export const hasCashback = (mode?: CashbackMode) =>
  mode === "CASH" || mode === "INVESTED";
export const takesCashbackRate = (mode?: CashbackMode) => mode === "CASH";
export const isInvestedCashback = (mode?: CashbackMode) => mode === "INVESTED";
export const cashbackSourceAccountsOf = <A extends CashbackSourceAccount>(
  accounts: A[] | undefined,
  currencyCode?: string,
): A[] =>
  (accounts ?? [])
    .filter(
      (account) =>
        cashbackAccountTypes.includes(account.accountType) &&
        (!currencyCode || account.currencyCode === currencyCode),
    )
    .sort(
      (a, b) =>
        Number(hasCashback(b.cashbackMode)) -
        Number(hasCashback(a.cashbackMode)),
    );
// A chosen source the accounts list no longer carries, e.g. an archived one, stays selectable.
export const cashbackSourceOptionsOf = ({
  accounts,
  currencyCode,
  selected,
}: CashbackSourceOptionsParams): CashbackSourceOption[] => {
  const options = cashbackSourceAccountsOf(accounts, currencyCode).map(
    (account) => ({ value: String(account.id), label: account.name }),
  );
  if (
    selected &&
    !accounts?.some((account) => String(account.id) === selected.value)
  ) {
    options.push(selected);
  }
  return options;
};
export const toOptionalNumber = (value?: string | number | null) =>
  isBlank(value) ? null : Number(value);
// Turning cashback on asks for a rate instead of silently enabling it at 0%.
export const cashbackFormValues = (
  settings: CashbackSettings,
): Required<CashbackFormValues> => ({
  cashbackMode: settings.cashbackMode ?? "NONE",
  cashbackRate:
    hasCashback(settings.cashbackMode) && settings.cashbackRate != null
      ? String(settings.cashbackRate)
      : "",
  cashbackMonthlyCap:
    settings.cashbackMonthlyCap == null
      ? ""
      : String(settings.cashbackMonthlyCap),
});
export const cashbackPayload = (
  values: CashbackFormValues,
): CashbackSettings => {
  const cashbackMode = values.cashbackMode ?? "NONE";
  if (!takesCashbackRate(cashbackMode)) {
    return { cashbackMode };
  }
  return {
    cashbackMode,
    cashbackRate: Number(values.cashbackRate || 0),
    cashbackMonthlyCap: toOptionalNumber(values.cashbackMonthlyCap),
  };
};
export const accountCashbackPayload = (
  accountType: AccountType,
  values: CashbackFormValues,
): CashbackSettings => {
  if (cashbackAccountTypes.includes(accountType)) {
    return cashbackPayload(values);
  }
  return {};
};

// Leaving CASH leaves invested buys and old corrections behind, so the block outlives the mode.
export const isCashbackSectionVisible = ({
  mode,
  invested,
  adjustmentCount,
}: CashbackSectionVisibilityParams) =>
  hasCashback(mode) || invested > 0 || adjustmentCount > 0;
export const isCashbackInUse = ({
  mode,
  received,
  invested,
  paidElsewhere,
}: CashbackInUseParams) =>
  hasCashback(mode) || received > 0 || invested > 0 || paidElsewhere > 0;
// Narrower than CashbackSection's rule on purpose: the savings hero only offers it where cashback lands in the balance.
export const canAddCashbackToBalance = ({
  mode,
  received,
}: AddCashbackToBalanceParams) => mode === "CASH" || received > 0;
